import React, { FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import { StandardButton, StandardSecondaryButton } from 'components/ui/Button';
import { CheckBox, Input } from 'components/ui/Form/Input';
import Note from 'components/ui/Note';
import Label from 'components/ui/Label';
import ExternalLink from 'components/ui/ExternalLink';
import { Table } from 'components/ui/Table';
import { Select, Option } from 'components/ui/Form/Select';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { ESICUpdateRequest } from 'api/companyOnboarding';
import api from 'api';
import { fetchLoggedInUser } from 'reducers/loggedInUser';
import { routePaths } from 'components/Routes/data';
import { AppError } from 'utils/AppError';
import { WarningAlert } from 'components/ui/Alert';
import Errors from 'components/ui/Form/Errors';
import Spinner from 'components/ui/Spinner';
import { trackSegment } from 'utils/segment';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import useFeature from 'hooks/useFeature';
import { OfficeLocationDropdown } from './OfficeLocationDropdown';
import {
  CredentialsFormData,
  getCredentialsInitialValue,
  getCredentialsPayload,
  getInitialCredsValue,
  validateCredentials,
} from './utils';
import { Alert, Button, PlusIcon } from '@razorpay/blade/components';
import { ResetLocalOverridingStyles } from './styles';
import { officeLocationQueryKeys } from 'components/OfficeLocation/PreviewOfficeLocations/data';

const ESI_ENABLED = 1;
const ESI_DISABLED = 0;

const EsicSetup = () => {
  const dispatch = useDispatch();
  const { isAdditionsDisabled } = useIsFeatureDisabledForRearch();
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);
  const [isESICPaymentEnabled, setIsESICPaymentEnabled] = useState(
    !!currentOrganization?.paymentOptions?.payEsi,
  );
  const [esiStatus, setEsiStatus] = useState(
    !!currentOrganization?.isEsi ? ESI_ENABLED : ESI_DISABLED,
  );
  const [shouldIncludeEmployerContributionInCtc, setShouldIncludeEmployerContributionInCtc] =
    useState(!!currentOrganization?.paymentOptions?.includeEmployerEsiContributionInCtc);
  const [shouldIncludeAdditions, setShouldIncludeAdditions] = useState(
    !!currentOrganization?.paymentOptions?.includePayrollAdditionsToESIWages,
  );
  const [esicUsername, setEsicUsername] = useState(
    currentOrganization?.externalCredentials?.esicUser || '',
  );
  const [esicPassword, setEsicPassword] = useState(
    currentOrganization?.externalCredentials?.esicPass || '',
  );
  const [showPassword, setShowPassword] = useState(false);

  const [multiCredentials, setMultiCredentials] = useState<CredentialsFormData>(
    getCredentialsInitialValue(
      currentOrganization?.externalCredentials?.esicLocationCredentials || {},
    ),
  );
  const [errorInEsicCredentials, setErrorInEsicCredentials] = useState<string>();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const links = useSelector(loggedInUserSelectors.referenceLinks);
  const { isEsicMultiLocationEnabled } = useFeature('esicMultiLocation');

  const { data: officeLocations } = useQuery({
    queryKey: [officeLocationQueryKeys.GET_OFFICE_LOCATIONS],
    queryFn: () =>
      api.officeLocation.fetchOfficeLocations({
        page: 1,
        limit: 100,
      }),
    enabled: isEsicMultiLocationEnabled,
  });

  const updateEsicMutation = useMutation(
    ({
      isESICEnabled,
      isESICPaymentEnabled,
      shouldIncludeEmployerContributionInCtc,
      shouldIncludeAdditions,
      esicUsername,
      esicPassword,
    }: ESICUpdateRequest) =>
      api.companyOnboarding.updateESICSetup({
        isESICEnabled,
        isESICPaymentEnabled,
        shouldIncludeEmployerContributionInCtc,
        shouldIncludeAdditions,
        esicUsername,
        esicPassword,
      }),
    {
      onSuccess: (data) => {
        trackSegment('onboarding.setup_compliance.esic_click');
        setIsRedirecting(true);
        dispatch(fetchLoggedInUser());
        window.location.href = '/';
      },
      onError: (error: typeof AppError) => {},
    },
  );

  const updateMultiEsicMutation = useMutation({
    mutationFn: api.companyOnboarding.updateMultiESICSetup,
    onSuccess: (data) => {
      setIsRedirecting(true);
      dispatch(fetchLoggedInUser());
      window.location.href = '/';
    },
    onError: (error: typeof AppError) => {},
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const isESICEnabled = esiStatus === ESI_ENABLED;
    if (isEsicMultiLocationEnabled) {
      setErrorInEsicCredentials(undefined);
      const errorMessage = validateCredentials(multiCredentials);
      if (errorMessage) {
        setErrorInEsicCredentials(errorMessage);
        return;
      }
      updateMultiEsicMutation.mutate({
        isESICEnabled,
        isESICPaymentEnabled: !isESICEnabled ? false : isESICPaymentEnabled,
        shouldIncludeEmployerContributionInCtc,
        shouldIncludeAdditions,
        esicCredentials:
          isESICEnabled && isESICPaymentEnabled
            ? getCredentialsPayload(multiCredentials)
            : undefined,
      });
    } else {
      updateEsicMutation.mutate({
        isESICEnabled,
        isESICPaymentEnabled: !isESICEnabled ? false : isESICPaymentEnabled,
        shouldIncludeEmployerContributionInCtc,
        shouldIncludeAdditions,
        esicUsername,
        esicPassword,
      });
    }
  };

  const handleAddCredentialRow = () => {
    setMultiCredentials((prev) => [...prev, getInitialCredsValue()]);
  };

  const isFormBeingSubmitted =
    updateEsicMutation.status === 'loading' || updateMultiEsicMutation.status === 'loading';

  if (isRedirecting) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <form onSubmit={handleSubmit} className="flex flex-col items-start">
        {updateEsicMutation.status === 'error' && (
          <WarningAlert className="mb-4">
            <Errors errorResponse={updateEsicMutation.error} />
          </WarningAlert>
        )}
        {updateMultiEsicMutation.status === 'error' && (
          <WarningAlert className="mb-4">
            <Errors errorResponse={updateMultiEsicMutation.error} />
          </WarningAlert>
        )}
        <Note>
          ESI is mandatory if the organization size is more than 10 employees. ESI should be
          deducted for employees whose salary is less than ₹21,000.
        </Note>
        <Label>ESI Status</Label>
        <Select
          value={esiStatus}
          onChange={(e) => {
            setEsiStatus(parseInt(e.target.value));
          }}
          disabled={isFormBeingSubmitted}
          className="mb-20">
          <Option value={ESI_ENABLED}>Enabled</Option>
          <Option value={ESI_DISABLED}>Disabled</Option>
        </Select>

        {esiStatus === ESI_ENABLED && (
          <>
            <Label>ESI payments</Label>
            <Note>
              Please note that disabling the payment of ESI does not disable the deduction of the
              same in your employees' payslips.
            </Note>
            <CheckBox
              id="esic-payment-enabled"
              label="ESI payments and filing (if applicable)"
              className="mb-8"
              value="esic-payment-enabled"
              checked={isESICPaymentEnabled}
              onChange={(event) => {
                setIsESICPaymentEnabled(event.target.checked);
              }}
              disabled={isFormBeingSubmitted}
            />

            {isESICPaymentEnabled && (
              <>
                <Label className="mt-16">ESIC</Label>
                <Note>
                  Your company's <ExternalLink href={links.linkEsic}>ESIC</ExternalLink> login is
                  required to pay ESIC dues and file returns.
                </Note>

                {isEsicMultiLocationEnabled ? (
                  <>
                    {errorInEsicCredentials && (
                      <WarningAlert className="mb-4">{errorInEsicCredentials}</WarningAlert>
                    )}
                    <Table className="w-full mb-10">
                      <thead>
                        <tr>
                          <th>Office Name *</th>
                          <th>Username *</th>
                          <th>Password *</th>
                        </tr>
                      </thead>
                      <tbody>
                        {multiCredentials.map((cred) => (
                          <tr key={cred.id}>
                            <td>
                              <OfficeLocationDropdown
                                value={cred.officeLocationId ?? ''}
                                onChange={({ values }) => {
                                  setMultiCredentials((prev) => {
                                    return prev.map((c) => {
                                      if (c.id === cred.id) {
                                        return { ...c, officeLocationId: values[0] };
                                      }
                                      return c;
                                    });
                                  });
                                }}
                              />
                            </td>
                            <td>
                              <Input
                                type="text"
                                name="esic-username"
                                disabled={isFormBeingSubmitted}
                                maxLength={255}
                                value={cred.esicUsername ?? ''}
                                onChange={(e) => {
                                  setMultiCredentials((prev) => {
                                    return prev.map((c) => {
                                      if (c.id === cred.id) {
                                        return { ...c, esicUsername: e.target.value };
                                      }
                                      return c;
                                    });
                                  });
                                }}
                              />
                            </td>
                            <td>
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                name="esic-password"
                                disabled={isFormBeingSubmitted}
                                maxLength={255}
                                value={cred.esicPassword ?? ''}
                                onChange={(e) => {
                                  setMultiCredentials((prev) => {
                                    return prev.map((c) => {
                                      if (c.id === cred.id) {
                                        return { ...c, esicPassword: e.target.value };
                                      }
                                      return c;
                                    });
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <ResetLocalOverridingStyles>
                      <Alert
                        title="Enable Payments for Multiple Locations"
                        description="Set up office locations and ESIC details to enable payment processing across locations. Don’t forget to add credentials for each location."
                        color="information"
                        isDismissible={false}
                        actions={{
                          secondary: {
                            text: 'Office Locations',
                            href: routePaths.officeLocation.create,
                            target: '_blank',
                          },
                        }}
                      />
                    </ResetLocalOverridingStyles>
                    <Button
                      isDisabled={
                        !officeLocations?.data?.length || officeLocations?.data?.length <= 1
                      }
                      marginY="spacing.6"
                      isFullWidth
                      icon={PlusIcon}
                      variant="secondary"
                      onClick={handleAddCredentialRow}>
                      Add Credentials
                    </Button>
                  </>
                ) : (
                  <Table className="w-full mb-10">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Password</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <Input
                            type="text"
                            name="esic-username"
                            disabled={isFormBeingSubmitted}
                            maxLength={255}
                            value={esicUsername}
                            onChange={(e) => setEsicUsername(e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            name="esic-password"
                            disabled={isFormBeingSubmitted}
                            maxLength={255}
                            value={esicPassword}
                            onChange={(e) => setEsicPassword(e.target.value)}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                <StandardSecondaryButton
                  type="button"
                  className="mb-16"
                  onClick={() => setShowPassword(!showPassword)}>
                  Click here to {showPassword ? 'hide' : 'show'} password
                </StandardSecondaryButton>
                <br />
              </>
            )}
            <Label className="mt-16">ESI settings</Label>
            {!isAdditionsDisabled && (
              <CheckBox
                id="payroll-additions-esi-wages"
                label="Include payroll additions and one-time payments to ESI wages."
                className="mb-8"
                value="payroll-additions-esi-wages"
                checked={shouldIncludeAdditions}
                onChange={(event) => {
                  setShouldIncludeAdditions(event.target.checked);
                }}
                disabled={isFormBeingSubmitted}
              />
            )}
            <CheckBox
              id="include-employer-contribution-esi-ctc"
              label="Include employer contribution to ESI in employee CTC"
              className="mb-16"
              value="include-employer-contribution-esi"
              checked={shouldIncludeEmployerContributionInCtc}
              onChange={(event) => {
                setShouldIncludeEmployerContributionInCtc(event.target.checked);
              }}
              disabled={isFormBeingSubmitted}
            />
          </>
        )}

        <StandardButton type="submit" isPending={isFormBeingSubmitted}>
          Save & Next
        </StandardButton>
      </form>
    </div>
  );
};

export default EsicSetup;
